import { useEffect, useState } from "react";
import { Bell, BellRing, Check, Loader2, X, Smartphone } from "lucide-react";
import { api } from "../lib/api";

const STORAGE_KEY = "ssc_push_subscription_id";

// Convert URL-safe base64 (the format our backend serves) to a Uint8Array
// suitable for `applicationServerKey`.
function urlBase64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const safe = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(safe);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function supportsPush() {
  return typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window;
}

const BODY_STYLES = [
  { v: "", label: "Any body style" },
  { v: "truck", label: "Truck" },
  { v: "suv", label: "SUV" },
  { v: "car", label: "Car" },
  { v: "ev", label: "EV" },
];

const NEW_USED = [
  { v: "either", label: "New or Used" },
  { v: "new", label: "New only" },
  { v: "used", label: "Used only" },
];

const FUELS = [
  { v: "", label: "Any fuel" },
  { v: "gas", label: "Gas" },
  { v: "electric", label: "Electric" },
  { v: "hybrid", label: "Hybrid" },
  { v: "diesel", label: "Diesel" },
];

export default function PushAlertSignup() {
  const [supported] = useState(supportsPush);
  const [permission, setPermission] = useState(() =>
    typeof Notification !== "undefined" ? Notification.permission : "default",
  );
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState(false);

  const [criteria, setCriteria] = useState({
    label: "",
    new_or_used: "either",
    make: "",
    model: "",
    body_style: "",
    fuel_type: "",
    max_price: "",
    max_mileage: "",
  });
  const [email, setEmail] = useState("");

  // Detect existing subscription on mount
  useEffect(() => {
    if (!supported) return;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (sub) setSubscribed(true);
      } catch { /* noop */ }
    })();
  }, [supported]);

  function setField(k, v) {
    setCriteria((c) => ({ ...c, [k]: v }));
  }

  async function enableAlerts(e) {
    e?.preventDefault?.();
    setBusy(true);
    setErr("");
    try {
      if (!supported) throw new Error("Your browser doesn't support push notifications.");

      // 1. Permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        throw new Error("Notification permission was denied. Enable it in your browser settings and try again.");
      }

      // 2. Get VAPID key from backend
      const { data: keyResp } = await api.get("/push/public-key");
      if (!keyResp?.public_key) throw new Error("Push isn't configured server-side yet.");

      // 3. Subscribe via PushManager
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(keyResp.public_key),
        });
      }

      // 4. POST subscription + criteria to backend
      const payload = {
        subscription: sub.toJSON(),
        email: email.trim() || undefined,
        criteria: {
          ...criteria,
          max_price: criteria.max_price ? Number(criteria.max_price) : null,
          max_mileage: criteria.max_mileage ? Number(criteria.max_mileage) : null,
        },
      };
      const { data: saved } = await api.post("/push/subscribe", payload);
      if (saved?.id) {
        try { localStorage.setItem(STORAGE_KEY, saved.id); } catch { /* noop */ }
      }
      setSubscribed(true);
      setOpen(false);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || ex?.message || "Couldn't enable alerts. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  async function disableAlerts() {
    setBusy(true);
    setErr("");
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.delete("/push/subscribe", { params: { endpoint: sub.endpoint } });
        await sub.unsubscribe();
      }
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
      setSubscribed(false);
    } catch (ex) {
      setErr(ex?.message || "Couldn't unsubscribe. Try again.");
    } finally {
      setBusy(false);
    }
  }

  if (!supported) {
    return (
      <div className="rounded-2xl border border-[#D9E1EA] bg-[#F4F7FB] p-4 text-sm text-[#475569] flex items-start gap-3" data-testid="push-unsupported">
        <Smartphone className="shrink-0 text-[var(--ssc-navy)]" size={18} />
        <div>
          <strong className="text-[var(--ssc-navy)]">Instant alerts work best in the installed app.</strong>{" "}
          Open this site in Chrome, Edge, or Firefox — or install the Scott Sells Chevys app on your phone — to get push alerts when a matching vehicle lands.
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-3xl border-2 border-[var(--ssc-blue)] bg-gradient-to-br from-[#E8F0FF] to-white p-5 md:p-7" data-testid="push-signup">
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-12 h-12 rounded-2xl bg-[var(--ssc-blue)] text-white flex items-center justify-center">
          {subscribed ? <BellRing size={22} /> : <Bell size={22} />}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-xl md:text-2xl font-black text-[var(--ssc-navy)] tracking-tight">
            {subscribed ? "Instant alerts are on" : "Get instant alerts when a match arrives"}
          </h3>
          <p className="text-[#475569] text-sm md:text-base mt-1">
            {subscribed
              ? "Your phone or browser will ping you the moment a vehicle matching your criteria hits Scott's lot. No email lag."
              : "Skip the inbox. Get a push notification the moment a vehicle matching your criteria lands — Web Push works on iOS, Android, and desktop."}
          </p>
        </div>
        {subscribed && (
          <button
            type="button"
            onClick={disableAlerts}
            disabled={busy}
            className="shrink-0 text-xs font-bold text-[#6b7c93] hover:text-[var(--ssc-red)]"
            data-testid="push-disable"
          >
            {busy ? <Loader2 size={14} className="animate-spin inline" /> : <><X size={14} className="inline -mt-0.5" /> Turn off</>}
          </button>
        )}
      </div>

      {!subscribed && !open && (
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="ssc-btn ssc-btn-primary"
            data-testid="push-open-form"
          >
            <Bell size={16} /> Set up my alert
          </button>
        </div>
      )}

      {subscribed && (
        <div className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-[#10a37f]">
          <Check size={16} /> You're subscribed on this device.
        </div>
      )}

      {open && !subscribed && (
        <form onSubmit={enableAlerts} className="mt-5 grid sm:grid-cols-2 gap-3" data-testid="push-form">
          <Field label="Nickname" optional>
            <input
              type="text"
              value={criteria.label}
              onChange={(e) => setField("label", e.target.value)}
              placeholder='e.g. "Trail Boss under $50k"'
              className="ssc-push-input"
              data-testid="push-label"
            />
          </Field>
          <Field label="New or Used">
            <select
              value={criteria.new_or_used}
              onChange={(e) => setField("new_or_used", e.target.value)}
              className="ssc-push-input"
              data-testid="push-newused"
            >
              {NEW_USED.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
          </Field>
          <Field label="Make">
            <input
              type="text"
              value={criteria.make}
              onChange={(e) => setField("make", e.target.value)}
              placeholder="Chevrolet"
              className="ssc-push-input"
              data-testid="push-make"
            />
          </Field>
          <Field label="Model">
            <input
              type="text"
              value={criteria.model}
              onChange={(e) => setField("model", e.target.value)}
              placeholder="Silverado, Trax, Equinox EV…"
              className="ssc-push-input"
              data-testid="push-model"
            />
          </Field>
          <Field label="Body style">
            <select
              value={criteria.body_style}
              onChange={(e) => setField("body_style", e.target.value)}
              className="ssc-push-input"
              data-testid="push-bodystyle"
            >
              {BODY_STYLES.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
          </Field>
          <Field label="Fuel">
            <select
              value={criteria.fuel_type}
              onChange={(e) => setField("fuel_type", e.target.value)}
              className="ssc-push-input"
              data-testid="push-fuel"
            >
              {FUELS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
          </Field>
          <Field label="Max price" optional>
            <input
              type="number"
              min={0}
              value={criteria.max_price}
              onChange={(e) => setField("max_price", e.target.value)}
              placeholder="50000"
              className="ssc-push-input"
              data-testid="push-maxprice"
            />
          </Field>
          <Field label="Max mileage" optional>
            <input
              type="number"
              min={0}
              value={criteria.max_mileage}
              onChange={(e) => setField("max_mileage", e.target.value)}
              placeholder="60000"
              className="ssc-push-input"
              data-testid="push-maxmiles"
            />
          </Field>
          <Field label="Email (optional, for cross-device alerts)" full>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="ssc-push-input"
              data-testid="push-email"
            />
          </Field>
          {err && (
            <div className="sm:col-span-2 text-sm font-bold text-[var(--ssc-red)]" data-testid="push-error">
              {err}
            </div>
          )}
          <div className="sm:col-span-2 flex gap-2 flex-wrap">
            <button
              type="submit"
              disabled={busy}
              className="ssc-btn ssc-btn-primary"
              data-testid="push-subscribe"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Bell size={16} />}
              {busy ? "Subscribing…" : "Turn on alerts"}
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="ssc-btn ssc-btn-secondary"
              data-testid="push-cancel"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {permission === "denied" && (
        <p className="mt-4 text-xs text-[var(--ssc-red)]">
          Notifications are blocked in your browser settings. To enable: open site settings → Notifications → Allow.
        </p>
      )}
    </div>
  );
}

function Field({ label, optional, full, children }) {
  return (
    <label className={`block ${full ? "sm:col-span-2" : ""}`}>
      <span className="text-xs font-bold uppercase tracking-wide text-[#6b7c93]">
        {label}{optional && <span className="text-[#9aa6b8] normal-case font-semibold"> · optional</span>}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
